import { useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import StatCard from "@/components/dashboard/StatCard";
import DashboardCustomizer from "@/components/dashboard/DashboardCustomizer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Briefcase,
  Users,
  CheckCircle2,
  AlertTriangle,
  Clock,
  TrendingUp,
  Calendar,
  ThumbsUp,
  ArrowRight,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { useActiveProjectsQuery } from "@/hooks/queries/useProjectsQuery";
import { useDashboardWidgets } from "@/hooks/useDashboardWidgets";
import { useDashboard } from "@/contexts/DashboardContext";
import { ProjectPhase, ProjectPhaseLabels, RiskLevel, RiskLevelLabels } from "@/types/project";
import type { Project as ApiProject } from "@/types/project";
import PendingInvitationsBanner from "@/components/organization/PendingInvitationsBanner";

const Dashboard = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const { stats: dashboardStats, isLoading: statsLoading, refresh } = useDashboard();

  // Refetch dashboard stats every time this page is viewed
  useEffect(() => {
    refresh();
  }, []);
  const { data: activeProjectsData, isLoading: activeProjectsLoading } = useActiveProjectsQuery();
  const apiActiveProjects = useMemo<ApiProject[]>(
    () => activeProjectsData?.items ?? [],
    [activeProjectsData]
  );
  const { widgets, updateWidgets, resetToDefault, isWidgetEnabled } = useDashboardWidgets();

  const phaseData = useMemo(() => {
    const phaseCounts: Record<number, number> = {};
    apiActiveProjects.forEach(p => {
      if (p.phase !== null && p.phase !== undefined) {
        phaseCounts[p.phase] = (phaseCounts[p.phase] ?? 0) + 1;
      }
    });
    return [
      { name: ProjectPhaseLabels[ProjectPhase.InitialReview],  count: phaseCounts[0] ?? 0 },
      { name: ProjectPhaseLabels[ProjectPhase.DueDiligence],   count: phaseCounts[1] ?? 0 },
      { name: ProjectPhaseLabels[ProjectPhase.Negotiation],    count: phaseCounts[2] ?? 0 },
      { name: ProjectPhaseLabels[ProjectPhase.Closing],        count: phaseCounts[3] ?? 0 },
    ];
  }, [apiActiveProjects]);

  const statusData = useMemo(() => {
    return [
      { name: t("active"),    value: dashboardStats?.activeProjects    ?? 0, color: "#10b981" },
      { name: t("onHold"),    value: dashboardStats?.onHoldProjects    ?? 0, color: "#f59e0b" },
      { name: t("cancelled"), value: dashboardStats?.cancelledProjects ?? 0, color: "#ef4444" },
      { name: t("done"),      value: dashboardStats?.doneProjects      ?? 0, color: "#6366f1" },
    ].filter(item => item.value > 0);
  }, [dashboardStats, t]);

  const activeProjects = useMemo(() => apiActiveProjects.slice(0, 5), [apiActiveProjects]);

  const getRiskBadgeFromEnum = (risk: RiskLevel | null) => {
    if (risk === null) return null;
    const styles: Record<RiskLevel, string> = {
      [RiskLevel.Low]: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
      [RiskLevel.Medium]: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
      [RiskLevel.High]: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
    };
    return <Badge className={styles[risk]}>{RiskLevelLabels[risk]}</Badge>;
  };

  // Check which stat widgets are enabled
  const enabledStats = [
    { id: "stat-total-projects", enabled: isWidgetEnabled("stat-total-projects"), component: (
      <StatCard key="total" title={t("totalProjects")} value={(dashboardStats?.totalProjects ?? 0).toString()} change={dashboardStats?.totalProjectsChangePercent ?? 0} subtitle={`${dashboardStats?.activeProjects ?? 0} ${t("active").toLowerCase()}`} isLoading={statsLoading} />
    )},
    { id: "stat-dd-progress", enabled: isWidgetEnabled("stat-dd-progress"), component: (
      <StatCard key="progress" title={t("avgDDProgress")} value={`${Math.round(dashboardStats?.averageDueDiligenceProgress ?? 0)}%`} change={dashboardStats?.averageDueDiligenceProgressChangePercent ?? 0} subtitle="" isLoading={statsLoading} />
    )},
    // { id: "stat-red-flags", enabled: isWidgetEnabled("stat-red-flags"), component: (
    //   <StatCard key="flags" title={t("redFlags")} value={(dashboardStats?.totalRedFlagProjects ?? 0).toString()} change={dashboardStats?.totalRedFlagProjectsChangePercent ?? 0} subtitle="" isLoading={statsLoading} />
    // )},
    { id: "stat-high-risk", enabled: isWidgetEnabled("stat-high-risk"), component: (
      <StatCard key="risk" title={`${t("high")} ${t("risk")}`} value={(dashboardStats?.totalHighRiskProjects ?? 0).toString()} change={dashboardStats?.totalHighRiskProjectsChangePercent ?? 0} subtitle="" isLoading={statsLoading} />
    )},
    { id: "stat-team", enabled: isWidgetEnabled("stat-team"), component: (
      <StatCard key="team" title={t("usersRolesTitle")} value={(dashboardStats?.totalUsersOnProjects ?? 0).toString()} change={dashboardStats?.totalUsersOnProjectsChangePercent ?? 0} subtitle="" isLoading={statsLoading} />
    )},
  ].filter(s => s.enabled);

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t("dashboardTitle")}</h1>
          <p className="text-muted-foreground">{t("dashboardSubtitle")}</p>
        </div>
        <DashboardCustomizer
          widgets={widgets}
          onWidgetsChange={updateWidgets}
          onResetToDefault={resetToDefault}
        />
      </div>

      <PendingInvitationsBanner />

      {/* Stats Grid */}
      {enabledStats.length > 0 && (
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-${Math.min(enabledStats.length, 6)} gap-4 mb-6`}>
          {enabledStats.map(stat => stat.component)}
        </div>
      )}

      {/* Main Content Grid - Row 1 */}
      {(isWidgetEnabled("list-active-projects") || isWidgetEnabled("chart-status")) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Active Projects List */}
          {isWidgetEnabled("list-active-projects") && (
            <Card className="lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Briefcase size={18} />
                  {t("activeProjects")}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate("/project")} className="gap-1">
                  {t("openProject")} <ArrowRight size={14} />
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {activeProjectsLoading && Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Skeleton className="w-2 h-2 rounded-full" />
                      <div className="space-y-1.5">
                        <Skeleton className="h-4 w-36" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </div>
                    <Skeleton className="h-5 w-14 rounded-full" />
                  </div>
                ))}
                {!activeProjectsLoading && activeProjects.length === 0 && (
                  <p className="text-sm text-muted-foreground">{t("noProjectsFound")}</p>
                )}
                {activeProjects.map((project) => (
                  <div
                    key={project.id}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors cursor-pointer"
                    onClick={() => navigate(`/project/${project.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <div>
                        <p className="font-medium text-sm">{project.targetName}</p>
                        <p className="text-xs text-muted-foreground">
                          {project.phase !== null ? ProjectPhaseLabels[project.phase] : ""}
                          {project.owner ? ` • ${project.owner}` : ""}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {getRiskBadgeFromEnum(project.risk)}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Status Pie Chart */}
          {isWidgetEnabled("chart-status") && (
            <Card className={!isWidgetEnabled("list-active-projects") ? "lg:col-span-3" : ""}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t("status")}</CardTitle>
              </CardHeader>
              <CardContent>
                {statsLoading ? (
                  <div className="space-y-3 mt-2">
                    <Skeleton className="h-48 w-full rounded-lg" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                ) : (
                <>
                <div className="h-48 relative">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={70}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold text-foreground">{dashboardStats?.totalProjects ?? 0}</span>
                    <span className="text-xs text-muted-foreground">{t("totalProjects")}</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-2 mt-2">
                  {statusData.map((item) => (
                    <div key={item.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-sm text-muted-foreground">{item.name}</span>
                      </div>
                      <span className="text-sm font-medium">{item.value}</span>
                    </div>
                  ))}
                </div>
                </>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Row 2 - Charts */}
      {(isWidgetEnabled("chart-phases") || isWidgetEnabled("list-deadlines")) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Phase Bar Chart */}
          {isWidgetEnabled("chart-phases") && (
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp size={18} />
                  {t("projectReports")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activeProjectsLoading ? (
                  <Skeleton className="h-64 w-full rounded-lg" />
                ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={phaseData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                        axisLine={{ stroke: "hsl(var(--border))" }}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                        axisLine={{ stroke: "hsl(var(--border))" }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                        }}
                        formatter={(value: number) => [value, t("totalProjects")]}
                      />
                      <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Empty State */}
      {enabledStats.length === 0 && 
       !isWidgetEnabled("list-active-projects") && 
       !isWidgetEnabled("chart-status") &&
       !isWidgetEnabled("chart-phases") &&
       !isWidgetEnabled("list-deadlines") &&
       !isWidgetEnabled("summary-team") &&
       !isWidgetEnabled("summary-approvals") && (
        <Card className="py-12">
          <CardContent className="text-center">
            <p className="text-muted-foreground mb-4">No widgets enabled. Click "Customize" to add widgets to your dashboard.</p>
            <DashboardCustomizer
              widgets={widgets}
              onWidgetsChange={updateWidgets}
              onResetToDefault={resetToDefault}
            />
          </CardContent>
        </Card>
      )}
    </DashboardLayout>
  );
};

export default Dashboard;
